import { useCallback } from 'react';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { dayLabel, dayRangeLabel, type DraftError } from '@/lib/marketing';

/**
 * What the three marketing pages — codes, automatic discounts, campaigns —
 * say the same way: why a field was refused, and when something runs.
 */

/** A refused field, in words. Undefined when the field is fine. */
export function useDraftError() {
  const t = useT();

  return useCallback(
    (error: DraftError | undefined): string | undefined => {
      switch (error) {
        case undefined:
          return undefined;
        case 'required':
          return t('required');
        case 'tooShort':
          return t('mktTooShort');
        case 'percent':
          return t('valuePercentHint');
        case 'amount':
          return t('mktAmountError');
        case 'whole':
          return t('mktWholeError');
        case 'date':
          return t('mktDateError');
        case 'endBeforeStart':
          return t('mktEndBeforeStart');
        case 'maxBelowMin':
          return t('mktMaxBelowMin');
        case 'tiersAscend':
          return t('promoTiersAscend');
        case 'giftNeedsMinimum':
          return t('promoGiftNeedsMinimum');
        case 'setNeedsTwo':
          return t('promoSetNeedsTwo');
      }
    },
    [t],
  );
}

/** "5 – 10 Oct 2026", "From 5 Oct 2026", "Until 10 Oct 2026", or a dash for no dates at all. */
export function useWindowLabel() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  return useCallback(
    (startsAt: string | null, endsAt: string | null): string => {
      if (startsAt && endsAt) return dayRangeLabel(startsAt, endsAt, locale);
      if (startsAt) return t('mktFrom', { date: dayLabel(startsAt, locale) });
      if (endsAt) return t('mktUntil', { date: dayLabel(endsAt, locale) });
      return t('dash');
    },
    [t, locale],
  );
}
